/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { useFeatureFlag } from '@/features/globalConfig';
import { useAlerts } from '@/hooks/useAlerts';
import { sessionStorage } from '@/utils/sessionStorage';
import { routePaths } from '@/constants/routePaths';

const SESSION_KEY_SHOWN = 'locationModalShown';
const PROMPT_DELAY_MS = 1200;

export function useStoreLocationPrompt(storeIsSet: boolean, isFixedHeader = false) {
  const locale = useLocale();
  const isPromptEnabled = useFeatureFlag('IS_STORE_LOCATION_PROMPT_ENABLED') === 'true';
  const pathname = usePathname();
  const isCheckoutPage = pathname === routePaths.checkout;
  const isUS = locale === countryCodes.us;
  const { alerts, clearAlert } = useAlerts();
  const storeLocatorRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [promptVisible, setPromptVisible] = useState(false);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setPromptVisible(false);
  }, [pathname]);

  useEffect(() => {
    if (!isPromptEnabled || isCheckoutPage || !isUS || !storeIsSet || isFixedHeader) {
      setPromptVisible(false);
      return;
    }
    if (alerts?.pendingAlerts.includes('storeLocationPrompt')) {
      clearAlert('storeLocationPrompt');
      sessionStorage.removeItem(SESSION_KEY_SHOWN);
    }
    if (sessionStorage.getItem(SESSION_KEY_SHOWN) === 'true') {
      return;
    }

    timerRef.current = setTimeout(() => {
      sessionStorage.setItem(SESSION_KEY_SHOWN, 'true');
      setPromptVisible(true);
    }, PROMPT_DELAY_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isPromptEnabled, isCheckoutPage, isUS, storeIsSet, isFixedHeader, alerts, clearAlert]);

  const closePrompt = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setPromptVisible(false);
  };

  return { promptVisible, storeLocatorRef, closePrompt };
}
